import {
  Card,
  createStyles,
  makeStyles,
  useDialogs,
  useIsMobile,
  ActionButton,
  BulkActionBar,
  BulkActionButton,
  ConfirmationDialog,
  Hidden,
  ResultsTable,
  TableCell,
  TableRow,
  useClientOptions,
  useUser,
  useUserPermissions,
  Link,
  Checkbox
} from '@paintscout/ui'
import type {
  TableCellProps,
  MuiTheme as Theme,
  StyleClasses,
  Auth0UserWithClaims,
  SelectableColumn
} from '@paintscout/ui'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import type { Company } from '@paintscout/api'
import { isUserAssigned, isUserOwner } from '@paintscout/util/builder'
import * as Sentry from '@sentry/react'
import MobilePersonRow from 'src/components/common/MobilePersonRow'
import find from 'lodash/find'
import { track } from 'shared/util/analytics'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import React, { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useCompanyMutations } from 'shared/hooks'

export interface CompanyTableProps {
  classes?: StyleClasses<typeof useStyles>
  companies: Array<Partial<Company>>
  loading?: boolean
  totalRows: number
  rowsPerPage: number

  checkboxes?: boolean
  actions?: {
    edit?: boolean
    delete?: boolean
  }

  sortKey?: string
  sort?: string

  page: number
  pages: number
  disablePagination?: boolean
  skipRefetchQueries?: boolean

  onFetchNextPage?: (page: number) => any
  onPageChange?: (page: number) => any
  onRequestSort?: (ev: React.MouseEvent, name: string, direction: 'asc' | 'desc') => void
  onDelete?: (ids: string[]) => any
}

export interface CompanyRow {
  _id: string
  _rev: string
  name: string
  email?: string
  owner: string
  assignedUsers?: string[]
  isAssigned: boolean
  isOwner: boolean
  updated: string
  created: string
  address?: string
  city?: string
  province?: string
  postal?: string
  phone_number?: string
  phone_ext?: string
}

export type CompanyResult = Partial<Company>

const useStyles = makeStyles<Theme>((theme: Theme) =>
  createStyles({
    root: {
      overflow: 'hidden'
    },
    tableRow: {
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P98
      }
    }
  })
)
/**
 * Displays companies in a ResultsTable
 */
function CompanyTable({
  skipRefetchQueries,
  disablePagination,
  onFetchNextPage,
  onRequestSort,
  actions = {},
  onPageChange,
  rowsPerPage,
  checkboxes,
  companies,
  onDelete,
  loading,
  sort,
  ...otherProps
}: CompanyTableProps) {
  const { permissionToEditAllCompanies, permissionToEditAssignedCompanies } = useUserPermissions()
  const { openDialog, dismissDialog } = useDialogs()
  const { deleteCompanies } = useCompanyMutations()
  const isMobile = useIsMobile()
  const { enqueueSnackbar } = useSnackbar()
  const { user, trialExpired } = useUser()
  const { options } = useClientOptions()
  const history = useHistory()
  const classes = useStyles()

  const dateFormat = options.options.dateFormat.momentValue
  const [selectedRows, setSelectedRows] = useState([])
  const rows = companies.map((company: CompanyResult) => mapCompanyToRow(company, user)).filter(Boolean)
  const columns: SelectableColumn[] = [
    { name: 'Name', field: 'name', sortable: true },
    { name: 'Email', field: 'email', sortable: true },
    { name: 'Owner', field: 'owner.firstName', sortable: true },
    { name: 'Updated', field: 'updated', sortable: true },
    { name: 'Created', field: 'created', sortable: true }
  ]

  useEffect(() => {
    setSelectedRows([])
  }, [companies])

  const createSortHandler = (property: string) => (event: React.MouseEvent) => {
    if (onRequestSort) {
      // get current sort direction
      const currentSort = getSortDirection(property) || 'asc'

      // flip the sort direction
      const newSort = currentSort === 'asc' ? 'desc' : 'asc'

      onRequestSort(event, property, newSort)
    }
  }

  const getSortDirection = (property: string) => {
    const currentSortDirection = sort && sort.startsWith('-') ? 'desc' : 'asc'
    const currentSortName = sort && sort.startsWith('-') ? sort.slice(1) : sort

    if (currentSortName === property) {
      return currentSortDirection
    }
  }

  const handlePageChange = useCallback(
    (page: number) => {
      const nextPageFirstIndex = (page - 1) * rowsPerPage
      const dataExists = !!companies[nextPageFirstIndex]

      if (onPageChange) {
        onPageChange(page)
      }

      if (!dataExists && onFetchNextPage) {
        onFetchNextPage(page)
      }
    },
    [onPageChange, onFetchNextPage, companies, rowsPerPage]
  )

  const handleRowClick = useCallback(
    (ev: React.MouseEvent<HTMLElement>, company: CompanyRow) => {
      const fullCompany = find(companies, { _id: company._id })
      history.push(`/people/companies/${encodeURIComponent(fullCompany._id)}`)
    },
    [companies]
  )

  const handleAction = useCallback(
    (action: string, companyRows: CompanyRow[]) => {
      const ids = companyRows.map((company) => company._id)
      const names = companyRows.map((company) => company.name?.trim()).filter((name): name is string => !!name)

      if (action === 'edit') {
        return history.push(`/people/companies/${encodeURIComponent(companyRows[0]._id)}`)
      } else {
        openDialog(ConfirmationDialog, {
          message: (
            <span>
              This will permanently delete{' '}
              {companyRows.length === 1 ? names[0] || 'this company' : 'all selected companies'}.
            </span>
          ),
          onConfirm: async () => {
            switch (action) {
              case 'delete':
                track('companies_bulk_deleted', { companyIds: ids, count: ids.length })
                deleteCompanies(ids, skipRefetchQueries).catch((error) => {
                  Sentry.captureException(error)
                  enqueueSnackbar('Unable to delete Company', { variant: 'error' })
                })
                if (onDelete) {
                  onDelete(ids)
                }
                break
            }
            setSelectedRows([])
            dismissDialog()
          },
          onCancel: dismissDialog
        })
      }
    },
    [handleRowClick, onDelete]
  )

  const rowActions = useCallback(
    (row: CompanyRow) => {
      const allowedActions = []

      if (
        actions.edit &&
        (row.isOwner || permissionToEditAllCompanies || (row.isAssigned && permissionToEditAssignedCompanies))
      ) {
        allowedActions.push({
          key: 'edit',
          label: 'Edit',
          icon: EditIcon
        })
      }

      if (
        actions.delete &&
        (row.isOwner || permissionToEditAllCompanies || (row.isAssigned && permissionToEditAssignedCompanies))
      ) {
        allowedActions.push({
          key: 'delete',
          label: 'Delete',
          icon: DeleteIcon
        })
      }
      return allowedActions
    },
    [actions]
  )

  const renderRow = useCallback(
    (row: CompanyRow) => {
      if (isMobile) {
        const addressLine = [row.address, row.city, row.province, row.postal].filter(Boolean).join(', ')
        const phoneLine = [row.phone_number, row.phone_ext ? `ext. ${row.phone_ext}` : null].filter(Boolean).join(' ')
        const actions = rowActions(row)

        return (
          <MobilePersonRow
            key={row._id}
            data-testid={`company-${row._id}`}
            className={classes.tableRow}
            name={row.name}
            addressLine={addressLine}
            phoneLine={phoneLine}
            actions={actions}
            actionsDisabled={trialExpired}
            onClick={(ev) => handleRowClick(ev, row)}
            onActionClick={(action) => handleAction(action, [row])}
          />
        )
      }

      return (
        <TableRow key={row._id} data-testid={`company-${row._id}`} className={classes.tableRow}>
          {checkboxes && (
            <TableCell padding="none" style={{ width: 68, minWidth: 68, maxWidth: 68 }}>
              <Checkbox
                checked={!!selectedRows.find((r) => r._id === row._id)}
                onChange={(ev, checked) => {
                  setSelectedRows(checked ? [...selectedRows, row] : selectedRows.filter((r) => r._id !== row._id))
                }}
              />
            </TableCell>
          )}
          <TableCellLink row={row}>{row.name}</TableCellLink>

          <Hidden smDown>
            <TableCellLink row={row}>
              {isMobile && row?.email?.length > 35 ? row.email.slice(0, 35) + '...' : row?.email || ''}
            </TableCellLink>
          </Hidden>

          <Hidden smDown>
            <TableCellLink row={row}>{row.owner}</TableCellLink>
          </Hidden>
          <Hidden smDown>
            <TableCellLink row={row}>
              {row.updated ? moment(parseInt(row.updated, 10)).format(dateFormat) : null}
            </TableCellLink>
          </Hidden>

          <TableCellLink row={row}>
            {row.created ? moment(parseInt(row.created, 10)).format(dateFormat) : null}
          </TableCellLink>

          {rowActions.length > 0 && (
            <TableCell align="right" style={{ width: 68, minWidth: 68, maxWidth: 68 }}>
              <ActionButton
                data-testid="actions"
                actions={rowActions(row)}
                disabled={rowActions(row).length === 0 || trialExpired}
                onActionClick={(ev, action) => handleAction(action, [row])}
              />
            </TableCell>
          )}
        </TableRow>
      )
    },
    [rowActions, selectedRows, isMobile, checkboxes]
  )

  return (
    <>
      <Card data-testid={'company-list'}>
        <ResultsTable
          pagesLoadedIndividually
          classes={{ root: classes.root }}
          noResultsMessage={'No companies found.'}
          columns={
            <TableRow>
              {!isMobile && (
                <>
                  {checkboxes && <TableCell isControl padding="none" />}
                  {columns.map((column) => {
                    const sortable = !!sort && !!column.sortable
                    return (
                      <TableCell
                        key={column.field}
                        align={column.align || 'left'}
                        fadeWhenInactive
                        sortable={sortable}
                        sortDirection={getSortDirection(column.field)}
                        onClick={sortable ? createSortHandler(column.field) : null}
                        style={{ minWidth: column.minWidth || undefined }}
                      >
                        {column.name}
                      </TableCell>
                    )
                  })}
                  {rowActions.length > 0 && <TableCell />}
                </>
              )}
            </TableRow>
          }
          renderRow={renderRow}
          rows={rows}
          rowsPerPage={rowsPerPage}
          disablePagination={disablePagination}
          loading={loading}
          {...otherProps}
          // otherProps wrappers
          onPageChange={handlePageChange}
        />
      </Card>
      <BulkActionBar
        itemName={'Company'}
        pluralItemName={'Companies'}
        selectedItems={selectedRows}
        onDeselectAll={() => setSelectedRows([])}
      >
        {actions.delete && (
          <BulkActionButton disabled={loading} icon={DeleteIcon} onClick={() => handleAction('delete', selectedRows)}>
            Delete
          </BulkActionButton>
        )}
      </BulkActionBar>
    </>
  )
}

function mapCompanyToRow(company: Partial<Company>, user: Auth0UserWithClaims): CompanyRow {
  if (!company) {
    return null
  }
  const owner = company.owner ? [company.owner?.firstName, company.owner?.lastName].filter(Boolean).join(' ') : ''
  const isAssigned = isUserAssigned(company, user.user_id)
  const isOwner = isUserOwner(company, user.user_id)

  return {
    _id: company._id,
    _rev: company._rev,
    assignedUsers: company.assignedUsers,
    name: company.name,
    email: company.email,
    isAssigned,
    isOwner,
    owner,
    updated: company.updated?.toString() ?? '',
    created: company.created?.toString() ?? '',
    address: company.address ?? '',
    city: company.city ?? '',
    province: company.province ?? '',
    postal: company.postal ?? '',
    phone_number: company.phone_number ?? '',
    phone_ext: company.phone_ext ?? ''
  }
}

/**
 * Renders a TableCell with a <Link> component to the company page
 */
function TableCellLink({ row, ...other }: { row: CompanyRow } & TableCellProps) {
  return (
    <TableCell
      component={(cProps: any) => (
        <Link to={{ pathname: `/people/companies/${encodeURIComponent(row._id)}` }} {...cProps} />
      )}
      {...other}
    />
  )
}

export default CompanyTable
